"use client";

import React, { useState, useRef } from "react";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent, richTextComponent } from "@/sanity/sanityPortableText";
import arrowSvg from "@/public/global/svg/arrow.svg";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import ChipStatue from "@/components/global/ui/ChipStatue";
import { MediaPlayerInstance } from "@vidstack/react";

import atlas from "@/public/global/atlas.png";
import ipad from "@/public/global/ipad.png";
import arrowRow from "@/public/home/arrow-row.svg";
import phemex from "@/public/home/phemex.png";
import play from "@/public/global/svg/play.svg";
import { PageCompetitionSchema } from "@/sanity/pages/PageCompetitionTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { GroqImage } from "@/helpers/Image";
import { motion, useScroll, useTransform } from "framer-motion";
import { CTAButton } from "@/shadcn/ui/button";

export default function WinSection({
  data,
  className,
}: {
  data: PageCompetitionSchema;
  className?: string;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });
  
  const yKnight = useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]);

  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="container flex flex-col items-center py-[8.6rem] lg:py-[21rem] relative">
        <div className="bg-white w-full lg:w-[min(153.875rem,100%)] border border-[#E5E5E5] flex flex-col gap-[3.12rem] lg:gap-[6.75rem] items-center py-[4.62rem] lg:py-[13.37rem] px-[3rem] lg:px-0">
          <div className="w-full flex flex-col items-center justify-center">
            {data?.winChip && (
              <ChipSerif text={data.winChip} />
            )}

            {data?.introText && (
              <div className="richtext w-[min(36.375rem,100%)] lg:w-[min(66.375rem,100%)] prose-p:text-[1.75rem] prose-p:max-w-[47.5rem]">
                <PortableText
                  value={data.introText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        
          <div className="flex flex-col items-center lg:flex-row gap-[4.75rem] lg:gap-[5.37rem]">
            {data?.winImage && (
              <motion.div
                className="flex-shrink-0 w-full lg:max-w-[60.75rem] lg:h-[56rem] rounded-[2.58525rem] lg:w-[56.25rem] overflow-hidden lg:block hidden"
                style={{ y: yKnight }}
              >
                <GroqImage
                  className="object-cover !w-full !h-full"
                  alt=""
                  quality={100}
                  src={data?.winImage}
                />
              </motion.div>
            )}

            {data?.winContent && (
              <div className="flex flex-col gap-[3rem] w-full max-w-[50rem] lg:h-[56rem]">
                <PortableText
                  value={data.winContent}
                  components={customRichTextComponent({
                    block: {
                      normal: ({ children }) => (
                        <p className="mb-0 leading-[3rem] text-[#103037]/70">
                          {children}
                        </p>
                      ),
                    },
                    list: {
                      bullet: ({ children }) => (
                        <ul className="flex flex-col gap-[3rem] text-[#1C1F22]">
                          {children}
                        </ul>
                      ),
                      number: ({ children }) => (
                        <ol className="flex flex-col gap-[2.25rem] lg:h-full">
                          {children}
                        </ol>
                      ),
                    },
                    listItem: {
                      bullet: ({ children }) => (
                        <li className="flex flex-row items-center leading-[3rem] text-[#1C1F22] mx-auto lg:mx-0">
                          <Image
                            src={arrowSvg}
                            alt="arrow"
                            className="flex-shrink-0 my-0 mr-[1.5rem] w-[2.35313rem]"
                            width={27}
                            height={27}
                          />
                          <span>{children}</span>
                        </li>
                      ),
                      number: ({ children, index }) => (
                        <li className="flex items-center gap-[3rem] leading-[2.7rem] py-[2.25rem] px-[3rem] bg-[#103037] rounded-[2.25rem] text-white lg:flex-1">
                          <span className="flex flex-shrink-0 justify-center items-center leading-none text-[#103037] text-[1.75rem] font-[500] tabular-nums px-[0.75rem] py-[1rem] bg-white rounded-[0.66rem] min-w-[3.25rem]">
                            {String((index ?? 0) + 1).padStart(2, "0")}
                          </span>
                          <span className="text-[1.75rem] lg:text-[2rem] leading-[135%]">{children}</span>
                        </li>
                      ),
                    },
                  })}
                />
              </div>
            )}
          </div>

          <div className="w-full flex flex-col gap-[3rem] items-center justify-center">
            {data?.winSubtitle && (
              <div className="richtext w-full prose-p:text-[1.75rem] prose-p:max-w-[56.625rem]">
                <PortableText
                  value={data.winSubtitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.winCTA && (
              <CTAButton data={data.winCTA} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
